// src/pages/buyer/GstCreditsPage.jsx
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { buyerGstCreditsApi } from '@/api/services'
import toast from 'react-hot-toast'
import { FileText, CheckCircle, IndianRupee, ArrowRight } from 'lucide-react'

const ITC_COLORS = {
  eligible:   'bg-green-100 text-green-700',
  claimed:    'bg-blue-100 text-blue-700',
  reversed:   'bg-red-100 text-red-700',
  ineligible: 'bg-gray-100 text-gray-600',
}

export default function GstCreditsPage() {
  const qc = useQueryClient()
  const [tab, setTab] = useState('ledger')
  const [fy, setFy] = useState('')
  const [period, setPeriod] = useState('')
  const [selected, setSelected] = useState([])

  const { data: ledgerData, isLoading } = useQuery({
    queryKey: ['gst-credits', fy, period],
    queryFn: () => buyerGstCreditsApi.ledger({ financial_year: fy || undefined, return_period: period || undefined }).then(r => r.data),
  })

  const { data: monthlyData } = useQuery({
    queryKey: ['gst-credits-monthly', period],
    queryFn: () => buyerGstCreditsApi.monthly({ return_period: period || undefined }).then(r => r.data),
    enabled: tab === 'monthly',
  })

  const claimMut = useMutation({
    mutationFn: (ids) => buyerGstCreditsApi.markClaimed({ credit_ids: ids }),
    onSuccess: () => {
      toast.success('Credits marked as claimed')
      setSelected([])
      qc.invalidateQueries({ queryKey: ['gst-credits'] })
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed to mark as claimed'),
  })

  const credits = ledgerData?.data?.credits ?? []
  const summary = ledgerData?.data?.summary ?? {}
  const monthly = monthlyData?.data ?? []

  const toggleSelect = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])
  }

  const selectAllEligible = () => {
    const eligibleIds = credits.filter(c => c.itc_status === 'eligible').map(c => c.id)
    setSelected(eligibleIds)
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">GST Input Tax Credits</h1>
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="FY e.g. 2025-26"
            value={fy}
            onChange={e => setFy(e.target.value)}
            className="border rounded px-3 py-1.5 text-sm w-32"
          />
          <input
            type="text"
            placeholder="Period e.g. 03-2026"
            value={period}
            onChange={e => setPeriod(e.target.value)}
            className="border rounded px-3 py-1.5 text-sm w-36"
          />
        </div>
      </div>

      {/* Summary cards */}
      {summary.total_gst !== undefined && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <SummaryCard label="Total GST Paid" value={fmt(summary.total_gst)} color="text-gray-900" />
          <SummaryCard label="Eligible ITC" value={fmt(summary.eligible)} color="text-green-600" />
          <SummaryCard label="Claimed" value={fmt(summary.claimed)} color="text-blue-600" />
          <SummaryCard label="CGST / SGST / IGST"
            value={`${fmt(summary.total_cgst)} / ${fmt(summary.total_sgst)} / ${fmt(summary.total_igst)}`}
            color="text-gray-700"
            small
          />
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b">
        <button
          onClick={() => setTab('ledger')}
          className={`px-4 py-2 font-medium border-b-2 transition ${tab === 'ledger' ? 'border-pink-500 text-pink-600' : 'border-transparent text-gray-500'}`}
        >
          Credit Ledger
        </button>
        <button
          onClick={() => setTab('monthly')}
          className={`px-4 py-2 font-medium border-b-2 transition ${tab === 'monthly' ? 'border-pink-500 text-pink-600' : 'border-transparent text-gray-500'}`}
        >
          Monthly Summary
        </button>
      </div>

      {tab === 'ledger' ? (
        <div className="space-y-3">
          {/* Bulk actions */}
          {selected.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2 flex items-center justify-between">
              <span className="text-sm text-blue-700">{selected.length} credit(s) selected</span>
              <button
                onClick={() => claimMut.mutate(selected)}
                disabled={claimMut.isPending}
                className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
              >
                <CheckCircle className="inline w-3.5 h-3.5 mr-1" /> Mark as Claimed
              </button>
            </div>
          )}

          {isLoading ? (
            <div className="text-center py-12 text-gray-400">Loading...</div>
          ) : credits.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No GST credits found</p>
              <p className="text-xs mt-1">Credits are recorded automatically when B2B orders with GSTIN are confirmed</p>
            </div>
          ) : (
            <>
              <div className="flex justify-end">
                <button onClick={selectAllEligible} className="text-xs text-blue-600 hover:text-blue-800">
                  Select all eligible
                </button>
              </div>
              <div className="bg-white rounded-lg border overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-left text-gray-600">
                      <th className="px-4 py-3 w-8"></th>
                      <th className="px-4 py-3 font-medium">Invoice</th>
                      <th className="px-4 py-3 font-medium">Supplier GSTIN</th>
                      <th className="px-4 py-3 font-medium">HSN</th>
                      <th className="px-4 py-3 font-medium text-right">Taxable</th>
                      <th className="px-4 py-3 font-medium text-right">CGST</th>
                      <th className="px-4 py-3 font-medium text-right">SGST</th>
                      <th className="px-4 py-3 font-medium text-right">IGST</th>
                      <th className="px-4 py-3 font-medium text-right">Total GST</th>
                      <th className="px-4 py-3 font-medium">Period</th>
                      <th className="px-4 py-3 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {credits.map(c => (
                      <tr key={c.id} className={`hover:bg-gray-50 ${selected.includes(c.id) ? 'bg-blue-50' : ''}`}>
                        <td className="px-4 py-3">
                          {c.itc_status === 'eligible' && (
                            <input
                              type="checkbox"
                              checked={selected.includes(c.id)}
                              onChange={() => toggleSelect(c.id)}
                              className="rounded border-gray-300"
                            />
                          )}
                        </td>
                        <td className="px-4 py-3 font-medium">{c.invoice_number || '—'}</td>
                        <td className="px-4 py-3 text-xs font-mono">{c.supplier_gstin || '—'}</td>
                        <td className="px-4 py-3">{c.hsn_code || '—'}</td>
                        <td className="px-4 py-3 text-right">{fmt(c.taxable_value)}</td>
                        <td className="px-4 py-3 text-right">{fmt(c.cgst)}</td>
                        <td className="px-4 py-3 text-right">{fmt(c.sgst)}</td>
                        <td className="px-4 py-3 text-right">{fmt(c.igst)}</td>
                        <td className="px-4 py-3 text-right font-medium">{fmt(c.total_gst)}</td>
                        <td className="px-4 py-3 text-xs">{c.return_period}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${ITC_COLORS[c.itc_status] || 'bg-gray-100'}`}>
                            {c.itc_status?.toUpperCase()}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {monthly.length === 0 ? (
            <div className="text-center py-12 text-gray-400">No monthly data available</div>
          ) : (
            <div className="bg-white rounded-lg border overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-left text-gray-600">
                    <th className="px-4 py-3 font-medium">HSN Code</th>
                    <th className="px-4 py-3 font-medium text-right">Taxable Value</th>
                    <th className="px-4 py-3 font-medium text-right">CGST</th>
                    <th className="px-4 py-3 font-medium text-right">SGST</th>
                    <th className="px-4 py-3 font-medium text-right">IGST</th>
                    <th className="px-4 py-3 font-medium text-right">Total GST</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {monthly.map((row, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium">{row.hsn_code || 'MISC'}</td>
                      <td className="px-4 py-3 text-right">{fmt(row.taxable_value)}</td>
                      <td className="px-4 py-3 text-right">{fmt(row.cgst)}</td>
                      <td className="px-4 py-3 text-right">{fmt(row.sgst)}</td>
                      <td className="px-4 py-3 text-right">{fmt(row.igst)}</td>
                      <td className="px-4 py-3 text-right font-medium">{fmt(row.total_gst)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function SummaryCard({ label, value, color, small }) {
  return (
    <div className="bg-white rounded-lg border p-4">
      <div className="text-xs text-gray-500 font-medium">{label}</div>
      <div className={`${small ? 'text-sm' : 'text-lg'} font-bold ${color} mt-1`}>{value}</div>
    </div>
  )
}

function fmt(n) {
  return `Rs.${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}
